import React from 'react';
import { useSelector } from 'react-redux';

import MiniOverlayModal from 'dpl/common/mini_overlay_modal/components/MiniOverlayModal';
import Button from 'dpl/common/design_system/Button';
import Icon from 'dpl/common/components/Icon';
import SmartImage from 'dpl/components/SmartImage';
import Input from 'dpl/common/design_system/Input';
import {
  FILTER_LOCATION_NAME,
  FILTER_GENDER,
  FILTER_POSTAL_CODE
} from 'dpl/constants/query_params';
import { capitalize } from 'dpl/shared/utils';
import useForm from 'dpl/shared/form/hooks/useForm';
import { CONSTRAINT_TYPES } from 'dpl/shared/validations/constraints';
import ErrorWrapper from 'dpl/form/components/ErrorWrapper';
import { isEmpty } from 'dpl/shared/utils/object';
import useResourceMutation from 'dpl/shared/fetching/hooks/useResourceMutation';
import { newsletterSignupMutationDefinition } from 'dpl/puppy_search_results/utils/queryDefinitions';
import { sendRudderstackEvent } from 'dpl/util/analytics';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEW_FEATURE_LOCATIONS,
  ANALYTICS_VIEW_FEATURES
} from 'dpl/constants/analytics';
import { FOREVER_DURATION } from 'dpl/shared/hooks/useSnoozed';
import { pushToastNotification } from 'dpl/shared/actions/toast_notifications';
import { TOAST_NOTIFICATIONS_TYPES } from 'dpl/shared/constants/shared';
import useCurrentUser from 'dpl/common/hooks/useCurrentUser';
import { SEARCH_ALERTS_FEATURE_KEY } from 'dpl/puppy_search_results/utils/constants';

import glassesIllustration from 'dpl/assets/fetch_glasses.png';

import { ALERTS_MODAL } from './SearchAlertsToggle';

const EMAIL_INPUT_NAME = 'email';
const GET_ALERTS_BUTTON_CTA = 'Get alerts';

function SuccessContent({ closeModal }: { closeModal: () => void }) {
  return (
    <div className="SuccessContent tc">
      <h4 className="font-32 lh-title mb3">You&apos;re signed up</h4>
      <p className="secondary font-16 mb8">
        We&apos;ll let you know as soon as breeders near you add new puppies and
        litters.
      </p>
      <Button
        className="font-16 w-100"
        size="default"
        type="button"
        onClick={closeModal}
      >
        Close
      </Button>
    </div>
  );
}

interface SignUpContentProps {
  alertsLocation?: string | null;
  breedName: string;
  genderFilter?: string | null;
  handleSubmit: (e: any) => void;
  formErrors?: any;
  didFormSubmissionFail?: boolean | null;
  setValue: (name: string, value: any) => void;
  formState?: any;
  isSubmitting: boolean;
  isLoggedIn?: boolean;
}

const signUpContentDefaultPropFormErrors = {};
const signUpContentDefaultPropFormState = {};
function SignUpContent({
  alertsLocation = null,
  breedName,
  genderFilter = null,
  handleSubmit,
  formErrors = signUpContentDefaultPropFormErrors,
  didFormSubmissionFail = null,
  setValue,
  formState = signUpContentDefaultPropFormState,
  isSubmitting,
  isLoggedIn = false
}: SignUpContentProps) {
  return (
    <div className="SignUpContent flex flex-column no-scrollbars">
      <div className="flex center mb6">
        <SmartImage
          lazy
          loadingClass="bg-light-gray"
          src={glassesIllustration}
          width="154px"
          height="140px"
        />
      </div>

      <h4 className="font-32 lh-title mb4 tc">
        Get alerts for new puppies from trusted breeders
      </h4>
      <div className="flex items-center br3 mb3 pv3 ph3 bg-gray-100">
        <Icon
          className="padding-v-6 padding-h-6 bg-blue-500 white br2 mr3"
          name="fetch-bell"
          height="40px"
          width="40px"
        />
        <div className="mr3">
          {alertsLocation && (
            <p className="fw-medium margin-b-2">Near {alertsLocation}</p>
          )}
          <p className="secondary font-16">
            {breedName}
            {genderFilter ? ` \u00b7 ${capitalize(genderFilter)}` : ''}
          </p>
        </div>
      </div>
      <form onSubmit={handleSubmit}>
        {!isLoggedIn && (
          <div className="mb8">
            <ErrorWrapper
              errors={formErrors.email}
              isShown={didFormSubmissionFail}
              newDesignSystemStyles
            >
              <Input
                placeholder="Enter your email"
                type="email"
                name={EMAIL_INPUT_NAME}
                onChange={(e: any) =>
                  setValue(EMAIL_INPUT_NAME, e.target.value)
                }
                className="w-100"
                value={formState[EMAIL_INPUT_NAME] || ''}
              />
            </ErrorWrapper>
          </div>
        )}
        <Button
          className="font-16 w-100"
          size="default"
          type="button"
          disabled={
            !isLoggedIn &&
            (isSubmitting ||
              !isEmpty(formErrors) ||
              !formState[EMAIL_INPUT_NAME])
          }
          onClick={(e: any) => handleSubmit(e)}
        >
          {GET_ALERTS_BUTTON_CTA}
        </Button>
      </form>
    </div>
  );
}

interface AlertsModalProps {
  openAlertsModal?: string | null;
  setToggleValue?: ((value: boolean) => void) | null;
  setOpenAlertsModal: (value: string | null) => void;
  setSnoozed: (key: string, duration: number) => void;
  analyticsView: string;
  onSuccess?: (() => void) | null;
}

export default function AlertsModal({
  openAlertsModal = null,
  setToggleValue = null,
  setOpenAlertsModal,
  setSnoozed,
  analyticsView,
  onSuccess = null
}: AlertsModalProps) {
  const {
    breedName,
    breedId,
    locationTitle,
    preappliedFilters: { cityName } = {}
  } = useSelector(({ server }: any) => server);
  const params = useSelector(({ queryParams }: any) => queryParams);

  const { isLoggedIn, value: currentUser } = useCurrentUser();

  const genderFilter = params[FILTER_GENDER];
  const locationFilter = params[FILTER_LOCATION_NAME];
  const zipCodeFilter = params[FILTER_POSTAL_CODE];

  const alertsLocation =
    locationFilter || locationTitle || cityName || zipCodeFilter;

  const { mutateAsync: signUpForNewsletter } = useResourceMutation({
    ...newsletterSignupMutationDefinition(),
    refetchAfter: 'success'
  });

  const {
    formState,
    formErrors,
    didSubmissionFail,
    setValue,
    isSubmitting,
    reset,
    handleSubmission
  } = useForm({
    onSubmit: async (formStateForSubmission: any) => {
      return signUpForNewsletter({
        body: {
          user:
            isLoggedIn && currentUser
              ? { email: currentUser?.data?.email }
              : formStateForSubmission,
          gender_interest: genderFilter,
          breed_interest_id: breedId,
          city_state: alertsLocation
        },
        refetchAfter: 'success'
      });
    },
    defaultFormState: {
      email: currentUser?.data?.email
    },
    mapFormStateToValidationSchema: () => ({
      email: CONSTRAINT_TYPES.EMAIL
    })
  });

  function handleCloseClick() {
    setOpenAlertsModal(null);

    if (openAlertsModal === ALERTS_MODAL.SIGN_UP) {
      // @ts-ignore
      reset(EMAIL_INPUT_NAME);
      setToggleValue && setToggleValue(false);
    }

    if (openAlertsModal === ALERTS_MODAL.SUCCESS) {
      setSnoozed(`${SEARCH_ALERTS_FEATURE_KEY}-${breedId}`, FOREVER_DURATION);
      onSuccess && onSuccess();
    }
  }

  async function handleSubmit(event: any) {
    event.preventDefault();
    const result = await handleSubmission();
    if (result?.response?.ok) {
      sendRudderstackEvent(ANALYTICS_EVENTS.SEARCH_ALERT_SUBMITTED_BY_BUYER, {
        button_cta: GET_ALERTS_BUTTON_CTA,
        view: analyticsView,
        view_feature: ANALYTICS_VIEW_FEATURES.SEARCH_ALERTS_MODULE,
        view_feature_location: ANALYTICS_VIEW_FEATURE_LOCATIONS.SEARCH_RESULTS
      });

      setOpenAlertsModal(ALERTS_MODAL.SUCCESS);
    } else {
      pushToastNotification({
        type: TOAST_NOTIFICATIONS_TYPES.ERROR,
        message: 'An error occurred. Please try again.'
      });
      handleCloseClick();
    }
  }

  return (
    <MiniOverlayModal
      className="AlertsModal"
      contentClassName="flex flex-column relative bg-white overflow-y-hidden pv14 ph16"
      maxWidth="600px"
      zIndexClass="z-99999"
      onCloseClick={handleCloseClick}
    >
      {openAlertsModal === ALERTS_MODAL.SIGN_UP && (
        <SignUpContent
          alertsLocation={alertsLocation}
          breedName={breedName}
          genderFilter={genderFilter}
          handleSubmit={handleSubmit}
          formErrors={formErrors}
          didFormSubmissionFail={didSubmissionFail}
          setValue={setValue}
          formState={formState}
          isSubmitting={isSubmitting}
          isLoggedIn={Boolean(isLoggedIn)}
        />
      )}
      {openAlertsModal === ALERTS_MODAL.SUCCESS && (
        <SuccessContent closeModal={handleCloseClick} />
      )}
    </MiniOverlayModal>
  );
}
